import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Shield, ShieldCheck, X, Upload, AlertTriangle } from "lucide-react";

interface ScanConfirmationModalProps {
  isOpen: boolean;
  onScanAndUpload: () => void;
  onUploadWithoutScan: () => void;
  onCancel: () => void;
  fileCount: number;
  fileNames: string[];
  scanRequired?: boolean; // If true, org policy enforces scanning
}

const ScanConfirmationModal: React.FC<ScanConfirmationModalProps> = ({
  isOpen,
  onScanAndUpload,
  onUploadWithoutScan,
  onCancel,
  fileCount,
  fileNames,
  scanRequired = false,
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="relative w-full max-w-[460px] mx-4 overflow-hidden text-left align-middle transition-all transform"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
              }}
            >
              <Shield size={22} color="#ffffff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#111827" }}>
                Security Scan
              </h2>
              <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                {fileCount} file{fileCount > 1 ? "s" : ""} selected
              </p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "0 24px 8px" }}>
          <p style={{ margin: "0 0 12px", fontSize: "14px", color: "#374151", lineHeight: "1.6" }}>
            Do you want to scan {fileCount > 1 ? "these files" : "this file"} for sensitive or prohibited content before uploading?
          </p>

          <div
            style={{
              padding: "12px 14px",
              borderRadius: "10px",
              backgroundColor: "#f5f3ff",
              border: "1px solid #ddd6fe",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <ShieldCheck size={16} color="#7c3aed" style={{ marginTop: "2px", flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: "12px", color: "#5b21b6", lineHeight: "1.5" }}>
                Scanning helps detect sensitive information such as credit card numbers, API keys, passwords, PII, and restricted content.
              </p>
            </div>
          </div>

          {/* File list preview */}
          {fileNames.length > 0 && (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                marginBottom: "16px",
                maxHeight: "100px",
                overflowY: "auto",
              }}
            >
              {fileNames.slice(0, 5).map((name, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: "12px",
                    color: "#4b5563",
                    padding: "2px 0",
                    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </div>
              ))}
              {fileNames.length > 5 && (
                <div style={{ fontSize: "11px", color: "#9ca3af", paddingTop: "4px" }}>
                  +{fileNames.length - 5} more file{fileNames.length - 5 > 1 ? "s" : ""}
                </div>
              )}
            </div>
          )}

          {/* Org policy notice */}
          {scanRequired && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "16px",
                padding: "8px 12px",
                borderRadius: "8px",
                backgroundColor: "#fffbeb",
                border: "1px solid #fde68a",
              }}
            >
              <AlertTriangle size={14} color="#d97706" />
              <span style={{ fontSize: "12px", color: "#92400e", fontWeight: 500 }}>
                Scanning is required by organization policy
              </span>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div
          style={{
            padding: "16px 24px 20px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {/* Primary: Scan & Upload */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onScanAndUpload();
            }}
            style={{
              width: "100%",
              padding: "10px 16px",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(124, 58, 237, 0.35)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(124, 58, 237, 0.25)";
            }}
          >
            <ShieldCheck size={16} />
            Scan & Upload
          </button>

          {/* Secondary: Upload Without Scan */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUploadWithoutScan();
            }}
            disabled={scanRequired}
            style={{
              width: "100%",
              padding: "10px 16px",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              backgroundColor: scanRequired ? "#f3f4f6" : "#ffffff",
              color: scanRequired ? "#9ca3af" : "#374151",
              fontSize: "13px",
              fontWeight: 500,
              cursor: scanRequired ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "all 0.2s ease",
              opacity: scanRequired ? 0.6 : 1,
            }}
            onMouseOver={(e) => {
              if (!scanRequired) e.currentTarget.style.backgroundColor = "#f9fafb";
            }}
            onMouseOut={(e) => {
              if (!scanRequired) e.currentTarget.style.backgroundColor = "#ffffff";
            }}
          >
            <Upload size={16} />
            Upload Without Scan
          </button>

          {/* Tertiary: Cancel */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            style={{
              width: "100%",
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              backgroundColor: "transparent",
              color: "#9ca3af",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "color 0.2s ease",
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#6b7280")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            Cancel
          </button>

          {/* Recommendation note */}
          {!scanRequired && (
            <p
              style={{
                margin: "4px 0 0",
                fontSize: "11px",
                color: "#9ca3af",
                textAlign: "center",
              }}
            >
              ✨ Recommended for security compliance
            </p>
          )}
        </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ScanConfirmationModal;
